import { Injectable, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthHttpService } from './auth-http.service';
import { LoggerService } from './logger.service';
import { ActiveActivationService } from './active-activation.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private inactivityTimer?: number;
  private warningTimer?: number;
  private countdownInterval?: number;
  private events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  private boundResetTimer = this.resetInactivityTimer.bind(this);
  
  showInactivityWarning = signal(false);
  remainingSeconds = signal(0);
  private isMonitoring = false;
  private lastActivityTime = Date.now();

  private authHttpService = inject(AuthHttpService);
  private logger = inject(LoggerService);
  private router = inject(Router);
  private activeActivation = inject(ActiveActivationService);

  startMonitoring(): void {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    this.resetInactivityTimer();
    this.events.forEach(event => 
      window.addEventListener(event, this.boundResetTimer, true)
    );
    this.logger.log('Monitoreo de inactividad iniciado');
  }

  stopMonitoring(): void {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    this.clearTimers();
    this.events.forEach(event => 
      window.removeEventListener(event, this.boundResetTimer, true)
    );
    this.logger.log('Monitoreo de inactividad detenido');
  }

  private resetInactivityTimer(): void {
    this.lastActivityTime = Date.now();
    this.clearTimers();
    
    const inactivityTime = environment.inactivityTimeMs || 900000; // 15 min default
    const warningTime = environment.inactivityWarningTimeMs || 60000; // 1 min default
    
    this.inactivityTimer = window.setTimeout(() => {
      this.handleInactivityExpired();
    }, inactivityTime);

    this.warningTimer = window.setTimeout(() => {
      this.showInactivityWarning.set(true);
      this.startCountdown(Math.ceil(warningTime / 1000));
      this.logger.log('Alerta de inactividad mostrada');
    }, inactivityTime - warningTime);
  }

  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = undefined;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  private startCountdown(seconds: number): void {
    this.remainingSeconds.set(seconds);
    this.countdownInterval = window.setInterval(() => {
      const remaining = this.remainingSeconds() - 1;
      this.remainingSeconds.set(remaining);
      if (remaining <= 0) {
        this.closeSession();
      }
    }, 1000);
  }

  continueSession(): void {
    this.showInactivityWarning.set(false);
    this.clearTimers();
    this.resetInactivityTimer();
    this.logger.log('Sesión continuada por el usuario');
  }

  closeSession(): void {
    this.showInactivityWarning.set(false);
    this.handleInactivityExpired();
  }

  isUserActive(): boolean {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    return timeSinceLastActivity < (environment.inactivityWarningTimeMs || 60000);
  }

  private handleInactivityExpired(): void {
    this.stopMonitoring();
    this.logger.log('Sesión cerrada por inactividad');

    // Si hay una activación prepago en curso, abandonarla antes del logout
    // para no dejar activaciones huérfanas en el backend.
    this.activeActivation.abandonIfActive().then(() => {
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId) {
        this.authHttpService.logout(sessionId).subscribe({
          next: (response) => {
            if (response.success) {
              localStorage.clear();
              this.router.navigate(['/login']);
            }
          },
          error: () => {
            localStorage.clear();
            this.router.navigate(['/login']);
          }
        });
      } else {
        localStorage.clear();
        this.router.navigate(['/login']);
      }
    });
  }
}
